import React, { memo } from 'react';
import { Box, Chip, Typography, Skeleton } from '@mui/material';
import type { DailyShiftSlot } from '../../../../../domain/dailyShiftSlot/DailyShiftSlot';

/**
 * ShiftSlotHeader Props
 */
interface ShiftSlotHeaderProps {
  /** シフト枠データ（ローディング中はundefined） */
  slots?: DailyShiftSlot[];
  /** ローディング状態 */
  isLoading?: boolean;
  /** 表示日付 */
  date: string;
}

/**
 * 枠集計情報の型
 */
interface SlotSummary {
  /** 募集枠数 */
  recruiting: number;
  /** 急募枠数 */
  urgent: number;
  /** 確定枠数 */
  confirmed: number;
}

/**
 * シフト枠から集計情報を計算
 */
const calculateSlotSummary = (slots: DailyShiftSlot[]): SlotSummary => {
  return slots.reduce(
    (acc, slot) => {
      acc.recruiting += slot.recruitmentSlots || 0;
      acc.urgent += slot.urgentSlots || 0;
      acc.confirmed += slot.confirmedSlots || 0;
      return acc;
    },
    { recruiting: 0, urgent: 0, confirmed: 0 }
  );
};

/**
 * シフト枠ヘッダーコンポーネント
 *
 * @description
 * シフト枠情報（募集/急募/確定）を集計して表示する。
 *
 * @param {ShiftSlotHeaderProps} props - コンポーネントProps
 * @returns {React.ReactElement} シフト枠ヘッダー
 *
 * @example
 * <ShiftSlotHeader
 *   slots={dailySlots}
 *   isLoading={false}
 *   date="2025-01-15"
 * />
 */
const ShiftSlotHeader: React.FC<ShiftSlotHeaderProps> = memo(
  ({ slots, isLoading = false, date }) => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1 }}>
          <Skeleton variant="rounded" width={80} height={24} />
          <Skeleton variant="rounded" width={80} height={24} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Box>
      );
    }

    const summary = slots ? calculateSlotSummary(slots) : { recruiting: 0, urgent: 0, confirmed: 0 };

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 1,
          backgroundColor: 'grey.50',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {date} 枠状況:
        </Typography>

        <Chip
          label={`募集中: ${summary.recruiting}`}
          size="small"
          color="primary"
          variant="outlined"
        />

        <Chip
          label={`急募: ${summary.urgent}`}
          size="small"
          color="error"
          variant={summary.urgent > 0 ? 'filled' : 'outlined'}
        />

        <Chip
          label={`確定: ${summary.confirmed}`}
          size="small"
          color="success"
          variant="outlined"
        />
      </Box>
    );
  }
);

ShiftSlotHeader.displayName = 'ShiftSlotHeader';

export { ShiftSlotHeader };
